/**
 * DrawerLayout — Slide-out drawer navigation shell.
 *
 * Two drawers replace the permanent sidebar:
 * - Tools (left)   — tool browser / search
 * - Settings (right) — step configuration
 *
 * Only one drawer open at a time. Escape, click-outside, or
 * opening another drawer closes the current one. 200ms ease transitions.
 */

import { X } from 'lucide-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { useLayoutStore } from '@/store/useLayoutStore';

interface DrawerLayoutProps {
  toolsContent?: React.ReactNode;
  settingsContent?: React.ReactNode;
  children: React.ReactNode;
}

/* ─── Drawer panel ────────────────────────────────────────────────────── */

interface DrawerPanelProps {
  open: boolean;
  onClose: () => void;
  position: 'left' | 'right' | 'bottom';
  title: string;
  width?: number;
  height?: number;
  children?: React.ReactNode;
}

const DrawerPanel: React.FC<DrawerPanelProps> = ({
  open,
  onClose,
  position,
  title,
  width = 400,
  height = 360,
  children,
}) => {
  const panelBaseClasses: Record<string, string> = {
    left: 'left-0 top-0 h-full border-r',
    right: 'right-0 top-0 h-full border-l',
    bottom: 'bottom-0 left-0 w-full border-t',
  };

  const closedTransform: Record<string, string> = {
    left: '-translate-x-full',
    right: 'translate-x-full',
    bottom: 'translate-y-full',
  };

  const panelStyle =
    position === 'bottom'
      ? { height: `${height}px` }
      : { width: `${width}px` };

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30 transition-opacity duration-200"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={cn(
          'fixed z-50 flex flex-col overflow-hidden',
          'bg-background border-border shadow-xl',
          'transition-transform duration-200 ease-out',
          panelBaseClasses[position],
          !open && closedTransform[position]
        )}
        style={panelStyle}
      >
        {/* Header */}
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-border px-4">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content — flex column so children can use flex-1 */}
        <div className="flex-1 min-h-0 flex flex-col">{children}</div>
      </div>
    </>
  );
};

/* ─── Main layout ─────────────────────────────────────────────────────── */

export const DrawerLayout: React.FC<DrawerLayoutProps> = ({
  toolsContent,
  settingsContent,
  children,
}) => {
  const { t } = useTranslation('common');
  const activeDrawer = useLayoutStore((s) => s.activeDrawer);
  const preferences = useLayoutStore((s) => s.drawerPreferences);
  const closeDrawer = useLayoutStore((s) => s.closeDrawer);

  // Escape to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeDrawer) {
        e.preventDefault();
        closeDrawer();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeDrawer, closeDrawer]);

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Drawer panels */}
      <DrawerPanel
        open={activeDrawer === 'tools'}
        onClose={closeDrawer}
        position="left"
        title={t('drawer.toolBrowser')}
        width={preferences.tools.width}
      >
        {toolsContent}
      </DrawerPanel>

      <DrawerPanel
        open={activeDrawer === 'settings'}
        onClose={closeDrawer}
        position="right"
        title={t('drawer.settings')}
        width={preferences.settings.width}
      >
        {settingsContent}
      </DrawerPanel>

      {/* Main content */}
      <div className="h-full w-full flex flex-col">{children}</div>
    </div>
  );
};
